/**
 * Retrieves the price of a seat assigned to a passenger, identified by their unique hash.
 *
 * Iterates through the seating sections and seats in the provided transport seating data,
 * finds the seat matching the given passenger's unique hash, and returns the price of the seat type.
 * If no matching seat or seat type is found, returns 0.
 *
 * @param passengerUniqueHash - The unique hash identifying the passenger.
 * @param activeTransportSeating - The transport seating data containing sections and seat types.
 * @returns The price of the seat assigned to the passenger, or 0 if not found.
 */

export const getSeatPriceByPassengerHash = (
  passengerUniqueHash: string,
  activeTransportSeating: Content.Components['TransportSeatingDto'],
): number => {
  if (
    !activeTransportSeating ||
    !activeTransportSeating.sections ||
    !activeTransportSeating.seatTypes
  ) {
    return 0;
  }

  for (const section of activeTransportSeating.sections) {
    for (const seat of section.seats) {
      if (seat.uniquePaxHash === passengerUniqueHash) {
        const seatType = activeTransportSeating.seatTypes.find(
          st => st.seatTypeId === seat.seatTypeId,
        );

        return seatType ? seatType.price : 0;
      }
    }
  }

  return 0;
};

/**
 * Retrieves a passenger object from the active transport seating data by matching the unique passenger hash.
 *
 * @param passengerUniqueHash - The unique hash identifying the passenger.
 * @param activeTransportSeating - The transport seating data containing the list of passengers.
 * @returns The matching `TransportPassengerDto` object if found, otherwise `null`.
 */
export const getPassengerByUniquePaxHash = (
  passengerUniqueHash: string,
  activeTransportSeating: Content.Components['TransportSeatingDto'],
): Content.Components['TransportPassengerDto'] | null => {
  if (!activeTransportSeating || !activeTransportSeating.passengers) {
    return null;
  }

  const passenger = activeTransportSeating.passengers.find(
    transportPassenger =>
      transportPassenger.uniquePaxHash === passengerUniqueHash,
  );

  return passenger ?? null;
};

/**
 * Retrieves the seat name assigned to a passenger based on their unique hash.
 *
 * Iterates through all sections and seats in the provided transport seating data to find
 * the seat associated with the specified passenger's unique hash.
 *
 * @param passengerUniqueHash - The unique hash identifying the passenger.
 * @param activeTransportSeating - The transport seating data containing sections and seats.
 * @returns The seat name if found, or `null` if the passenger does not have an assigned seat.
 */
export const getSeatNameByPassengerHash = (
  passengerUniqueHash: string,
  activeTransportSeating: Content.Components['TransportSeatingDto'],
): string | null => {
  if (!activeTransportSeating || !activeTransportSeating.sections) {
    return null;
  }

  for (const section of activeTransportSeating.sections) {
    for (const seat of section.seats) {
      if (seat.uniquePaxHash === passengerUniqueHash) {
        return seat.seatName;
      }
    }
  }

  return null;
};

const SEAT_WIDTH = 30;

export type RowLabelPosition = {
  rowNumber: number;
  x: number;
  y: number;
};

/**
 * Calculate row label positions (row number between left/right seat blocks)
 * @param seats - array of seat objects
 * @param rowGap - tolerance for grouping seats with similar Y values
 */
export const getRowLabelPositions = (
  seats: Content.Components['SeatDto'][],
): RowLabelPosition[] => {
  // Group strictly by Y
  const rowsMap: Record<number, Content.Components['SeatDto'][]> = {};

  seats.forEach(seat => {
    if (!rowsMap[seat.positionY]) {
      rowsMap[seat.positionY] = [];
    }

    rowsMap[seat.positionY].push(seat);
  });

  const labels: RowLabelPosition[] = Object.entries(rowsMap).map(
    ([yStr, rowSeats]) => {
      const y = Number(yStr);

      // Row number = digits from seatName
      const rowNumber = parseInt(rowSeats[0].seatName.replace(/\D/g, ''), 10);

      // Group by seat letters
      const leftBlock = rowSeats.filter(s =>
        /[ABC]$/.test(s.seatName.toUpperCase()),
      );

      // Fallback: if no A–C seats exist, just take all seats
      const effectiveLeftBlock = leftBlock.length > 0 ? leftBlock : rowSeats;

      // Find rightmost seat in left block
      const leftMax = Math.max(...effectiveLeftBlock.map(s => s.positionX));

      // Place label offset to the right of left block by seat width
      const x = leftMax + SEAT_WIDTH;

      return { rowNumber, x, y };
    },
  );

  return labels.sort((a, b) => a.y - b.y);
};

export type SeatBlockLinePositions = {
  leftLine: {
    x: number;
    y1: number;
    y2: number;
  };
  rightLine: {
    x: number;
    y1: number;
    y2: number;
  };
};

/**
 * Calculate line positions to the left and right of all seats combined.
 * Each line can have adjustable left/right offsets and height.
 *
 * @param seats - array of seat objects
 * @param leftOffset - pixels to offset the left line from the leftmost seat
 * @param rightOffset - pixels to offset the right line from the rightmost seat
 * @param lineHeight - height of the line in pixels
 * @returns { leftLine: { x: number; y: number; height: number }, rightLine: { x: number; y: number; height: number } }
 */
export const getSeatBlockLinePositions = (
  seats: Content.Components['SeatDto'][],
  leftLineOffset: number = 0,
  rightLineOffset: number = 0,
): SeatBlockLinePositions | null => {
  if (!seats || seats.length === 0) return null;

  const leftmostSeat = seats.reduce(
    (min, seat) => (seat.positionX < min.positionX ? seat : min),
    seats[0],
  );
  const rightmostSeat = seats.reduce(
    (max, seat) => (seat.positionX > max.positionX ? seat : max),
    seats[0],
  );
  const topmostSeat = seats.reduce(
    (min, seat) => (seat.positionY < min.positionY ? seat : min),
    seats[0],
  );
  const bottommostSeat = seats.reduce(
    (max, seat) => (seat.positionY > max.positionY ? seat : max),
    seats[0],
  );

  return {
    leftLine: {
      x: leftmostSeat.positionX + leftLineOffset,
      y1: topmostSeat.positionY - SEAT_WIDTH,
      y2: bottommostSeat.positionY + SEAT_WIDTH,
    },
    rightLine: {
      x: rightmostSeat.positionX + rightLineOffset + SEAT_WIDTH,
      y1: topmostSeat.positionY - SEAT_WIDTH,
      y2: bottommostSeat.positionY + SEAT_WIDTH,
    },
  };
};
